// src/pages/CorkBoard.jsx
//
// The Cork Board: the manuscript's story elements as index cards pinned to a
// board, with red thread between the ones that are related.
//
// **This is a second view of the editor's Story Elements tab, not a second
// feature.** Same nodes, same edges, same endpoints, same pure helpers in
// utils/storyElements.js. The tab is a list and answers "what is in this book";
// the board is a surface and answers "how does it hang together". Anything that
// changes the *model* has to change both, which is why nothing here owns a
// shape of its own — the page holds the board exactly as the API returns it.
//
// **Positions are shared.** x/y live on the node, not per user, so a card an
// author moves is where their co-author finds it. See the note on MAX_COORD in
// backend/routes/story_elements.py.
//
// **Chapters are not cards.** They are derived from the manuscript HTML rather
// than stored (outline.js is the source of truth), so a relationship pointing
// at a chapter is listed in the side panel but has no pin to tie a string to.
// See corkBoard.js.
//
// Reached at /manuscript/:id/cork-board, beside /manuscript/:id/structure, and
// keeps the app's global <Navbar /> by staying out of OWN_NAV_ROUTES in App.js.
// Colours come from corkTokens.js, which maps onto the app's primary and
// secondary rather than the prototype's palette.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { API_BASE } from '../utils/api';
import { storyElementApi } from '../utils/readerApi';
import { chaptersFromHtml } from '../utils/corkBoard';
import { serverExplanation } from '../utils/apiError';
import {
  CB, FONT, TYPE_COLOR, eyebrowSx, BP, FLOATING_PANEL_W, DOCKED_PANEL_W,
} from '../components/corkboard/corkTokens';
import CorkBoardStage from '../components/corkboard/CorkBoardStage';
import FlowDiagram from '../components/corkboard/FlowDiagram';
import CorkDetailsPanel from '../components/corkboard/CorkDetailsPanel';
import CorkCreatePanel from '../components/corkboard/CorkCreatePanel';
import CorkAddCluster from '../components/corkboard/CorkAddCluster';
import ManuscriptPreview from '../components/corkboard/ManuscriptPreview';

const EMPTY_BOARD = { nodes: [], edges: [] };

// Two ways of looking at one board. The corkboard is spatial — where things sit
// is the author's own arrangement; the flow diagram is structural — where things
// sit is derived from the relationships. Neither is a mode: the selection, the
// side panel and the "show all connections" toggle carry across both.
const VIEWS = [
  { key: 'board', label: '📌 Corkboard' },
  { key: 'flow', label: '⋮⋮ Flow Diagram' },
];

export default function CorkBoard() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [draft, setDraft] = useState(null);
  const [board, setBoard] = useState(EMPTY_BOARD);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');

  const [view, setView] = useState('board'); // 'board' | 'flow'
  const [selectedId, setSelectedId] = useState(null);
  const [creatingType, setCreatingType] = useState(null);
  const [showAllConnections, setShowAllConnections] = useState(false);
  // Only meaningful below 900px, where the panel floats. Above it the panel is
  // docked and this is ignored — same shape as the editor's `railOpen`.
  const [panelOpen, setPanelOpen] = useState(false);

  const accessToken = localStorage.getItem('rv_access_token');

  // --- load ---------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError('');
      try {
        const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
        // The draft is fetched for its `content` alone — the manuscript HTML the
        // chapters are derived from. The board comes from its own endpoint.
        const [draftRes, elements] = await Promise.all([
          fetch(`${API_BASE}/api/drafts/${id}`, { credentials: 'omit', headers }),
          storyElementApi.get(id),
        ]);
        if (!draftRes.ok) {
          throw new Error(draftRes.status === 403
            ? 'You do not have access to this manuscript.'
            : 'That manuscript could not be loaded.');
        }
        const draftData = await draftRes.json();
        if (cancelled) return;
        setDraft(draftData);
        setBoard(elements?.storyElements || EMPTY_BOARD);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'That board could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [id, accessToken]);

  const chapters = useMemo(() => chaptersFromHtml(draft?.content), [draft?.content]);

  const selected = useMemo(
    () => board.nodes.find((n) => String(n._id) === String(selectedId)) || null,
    [board.nodes, selectedId],
  );

  // --- writes -------------------------------------------------------------
  //
  // Every mutation is optimistic: the board applies the change locally and then
  // tells the server. A card that waited for a round trip before moving would
  // feel broken on a drag, and the same rule applied unevenly is worse than
  // either rule applied consistently. `reload` is the recovery path — a failed
  // write puts the board back to what the server actually holds rather than
  // leaving the author looking at a change that did not happen.

  const reload = useCallback(async () => {
    try {
      const elements = await storyElementApi.get(id);
      setBoard(elements?.storyElements || EMPTY_BOARD);
    } catch { /* the error banner is already up */ }
  }, [id]);

  /**
   * Report a failed write, and put the board back to what the server holds.
   *
   * The server's own message is shown alongside ours. The first version of this
   * swallowed it and said only "that could not be saved", which is enough for
   * an author to know something went wrong and useless to anyone trying to find
   * out what — a 403 and a malformed payload read identically.
   */
  const failed = useCallback((message, err) => {
    setSaveError([message, serverExplanation(err)].filter(Boolean).join(' '));
    reload();
  }, [reload]);

  /** Local only — keeps the card on the board in step with the panel as you type. */
  const patchNode = useCallback((nodeId, fields) => {
    setBoard((prev) => ({
      ...prev,
      nodes: prev.nodes.map((n) => (String(n._id) === String(nodeId) ? { ...n, ...fields } : n)),
    }));
  }, []);

  const saveNode = useCallback(async (nodeId, fields) => {
    try {
      setSaveError('');
      await storyElementApi.updateNode(id, nodeId, fields);
    } catch (err) {
      failed('That change could not be saved.', err);
    }
  }, [id, failed]);

  const moveNode = useCallback(async (nodeId, { x, y }) => {
    patchNode(nodeId, { x, y });
    try {
      setSaveError('');
      await storyElementApi.moveNode(id, nodeId, { x, y });
    } catch (err) {
      failed('That card could not be moved.', err);
    }
  }, [id, patchNode, failed]);

  const removeNode = useCallback(async (nodeId) => {
    const ref = `node:${nodeId}`;
    setBoard((prev) => ({
      nodes: prev.nodes.filter((n) => String(n._id) !== String(nodeId)),
      // The server removes the relationships in the same write; mirroring it
      // here keeps the strings from pointing at a card that is already gone.
      edges: prev.edges.filter((e) => e.from !== ref && e.to !== ref),
    }));
    setSelectedId(null);
    try {
      setSaveError('');
      await storyElementApi.removeNode(id, nodeId);
    } catch (err) {
      failed('That element could not be removed.', err);
    }
  }, [id, failed]);

  const addRelationship = useCallback(async ({ from, to, label }) => {
    try {
      setSaveError('');
      const edge = await storyElementApi.createEdge(id, { from, to, label });
      setBoard((prev) => ({ ...prev, edges: [...prev.edges, edge] }));
    } catch (err) {
      failed('That connection could not be added.', err);
    }
  }, [id, failed]);

  const removeRelationship = useCallback(async (edgeId) => {
    setBoard((prev) => ({
      ...prev,
      edges: prev.edges.filter((e) => String(e._id) !== String(edgeId)),
    }));
    try {
      setSaveError('');
      await storyElementApi.removeEdge(id, edgeId);
    } catch (err) {
      failed('That connection could not be removed.', err);
    }
  }, [id, failed]);

  const createNode = useCallback(async ({ type, name, desc, connectTo, relLabel }) => {
    try {
      setSaveError('');
      // No x/y: a new card takes its place from the same deterministic layout
      // every unplaced element uses, and gets a stored position the first time
      // the author drags it. Dropping it at a guessed coordinate would be the
      // one card on the board whose position nobody chose.
      const node = await storyElementApi.createNode(id, { type, name, desc });
      const edge = connectTo
        ? await storyElementApi.createEdge(id, {
          from: `node:${node._id}`, to: connectTo, label: relLabel,
        })
        : null;
      setBoard((prev) => ({
        nodes: [...prev.nodes, node],
        edges: edge ? [...prev.edges, edge] : prev.edges,
      }));
      setCreatingType(null);
      setSelectedId(node._id);
    } catch (err) {
      failed('That element could not be created.', err);
    }
  }, [id, failed]);

  const selectCard = useCallback((nodeId) => {
    setSelectedId(nodeId);
    // Selecting a card is a request to look at it, which the create form would
    // be covering.
    if (nodeId) setCreatingType(null);
    // Below 900px the panel is parked off-screen, so a selection that did not
    // open it would look like a click that did nothing — the details it just
    // loaded would be behind the edge of the screen.
    setPanelOpen(Boolean(nodeId));
  }, []);

  // One empty state, two grounds: the corkboard is dark cork, the flow diagram
  // is white. Written once rather than per view so the wording cannot drift.
  const onCork = view === 'board';
  const emptyState = (
    <Box sx={{
      position: 'absolute', inset: 0, display: 'flex',
      flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', px: 3, pointerEvents: 'none', zIndex: 3,
    }}>
      <Typography sx={{
        fontFamily: FONT.serif, fontSize: 19, fontWeight: 600,
        color: onCork ? 'rgba(255,255,255,0.92)' : CB.black,
      }}>
        Nothing pinned yet
      </Typography>
      <Typography sx={{
        fontFamily: FONT.mono, fontSize: 11.5, mt: 1, maxWidth: 380, lineHeight: 1.7,
        color: onCork ? 'rgba(255,255,255,0.75)' : CB.gray,
      }}>
        Add an actor, a scene or an artifact with the buttons in the corner.
        Anything you have already created in the editor&rsquo;s Story Elements
        tab will appear here too.
      </Typography>
    </Box>
  );

  // --- render -------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', py: 10, px: 3, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: FONT.serif, fontSize: 20, fontWeight: 600 }}>
          {loadError}
        </Typography>
        <Button onClick={() => navigate('/library')} startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to the library
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: CB.lavender, minHeight: '100vh' }}>
      {/* --- masthead --- */}
      <Box sx={{ backgroundColor: CB.purpleDeep, color: CB.white }}>
        <Box sx={{
          maxWidth: 1320, mx: 'auto', px: 3.5, py: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 1.5,
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="span" sx={{ ...eyebrowSx, color: CB.goldLight }}>
              Cork board
            </Typography>
            <Typography sx={{
              fontFamily: FONT.serif, fontWeight: 600, fontSize: 21, mt: 0.25,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {draft?.title || 'Untitled manuscript'}
            </Typography>
          </Box>
          <Button
            onClick={() => navigate(`/editor/${id}`)}
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={{
              fontFamily: FONT.mono, fontSize: 11.5, textTransform: 'none',
              color: 'rgba(255,255,255,0.8)', border: `1px solid ${CB.lineLt}`,
              px: 1.75, py: 0.9,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: CB.white },
            }}
          >
            Manuscript Editor
          </Button>
        </Box>
      </Box>

      {/* --- toolbar --- */}
      <Box sx={{ backgroundColor: CB.white, borderBottom: `1px solid ${CB.lineSoft}` }}>
        <Box sx={{
          maxWidth: 1320, mx: 'auto', px: 3.5, py: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 1.75,
        }}>
          <Box sx={{ maxWidth: 460 }}>
            <Typography sx={{
              fontFamily: FONT.serif, fontWeight: 600, fontSize: 16, fontStyle: 'italic',
            }}>
              Story Builder
            </Typography>
            {/* The explanation is the first thing to go on a phone. It is
                orientation you need once, and the board below it is what the
                screen is for — three lines of prose push it under the fold. */}
            <Typography sx={{
              fontFamily: FONT.sans, fontSize: 12, color: CB.gray, lineHeight: 1.45, mt: 0.5,
              [BP.phone]: { display: 'none' },
            }}>
              Pin your actors, scenes and artifacts to the board and see how they
              connect. Everything here is the same set of elements as the editor's
              Story Elements tab.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {VIEWS.map(({ key, label }) => (
              <Box
                key={key}
                component="button"
                type="button"
                aria-pressed={view === key}
                onClick={() => setView(key)}
                sx={{
                  fontFamily: FONT.mono, fontSize: 11.5, letterSpacing: '.03em',
                  textTransform: 'uppercase', cursor: 'pointer',
                  px: 2.25, py: 1.1, borderRadius: '5px',
                  border: `1.5px solid ${view === key ? CB.purple : CB.lineSoft}`,
                  backgroundColor: view === key ? CB.purple : CB.white,
                  color: view === key ? CB.white : CB.gray,
                  fontWeight: view === key ? 600 : 400,
                  '&:hover': { borderColor: CB.purpleSoft },
                }}
              >
                {label}
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {['actor', 'scene', 'artifact'].map((type) => (
              <Box key={type} sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                fontFamily: FONT.mono, fontSize: 10.5, color: CB.gray,
                // The legend goes too: every card already prints its own type,
                // so on a phone this is a key to something not in question.
                [BP.phone]: { display: 'none' },
              }}>
                <Box sx={{
                  width: 10, height: 10, borderRadius: '3px',
                  backgroundColor: TYPE_COLOR[type],
                }} />
                {type === 'scene' ? 'Scene / Chapter' : type[0].toUpperCase() + type.slice(1)}
              </Box>
            ))}
            <Box
              component="button"
              type="button"
              aria-pressed={showAllConnections}
              onClick={() => setShowAllConnections((v) => !v)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.9, cursor: 'pointer',
                fontFamily: FONT.mono, fontSize: 10.5, px: 1.6, py: 0.9,
                borderRadius: '999px',
                border: `1px solid ${showAllConnections ? CB.purpleSoft : CB.lineSoft}`,
                backgroundColor: showAllConnections ? CB.lavender : CB.white,
                color: showAllConnections ? CB.purple : CB.gray,
                fontWeight: showAllConnections ? 600 : 400,
                '&:hover': { borderColor: CB.purpleSoft },
              }}
            >
              <Box sx={{
                width: 8, height: 8, borderRadius: '50%',
                backgroundColor: showAllConnections ? CB.purple : CB.lineSoft,
              }} />
              Show all connections
            </Box>
          </Box>
        </Box>
      </Box>

      {saveError ? (
        <Box sx={{
          backgroundColor: '#FBEEF0', borderBottom: '1px solid #EBCBD4',
          px: 3.5, py: 1.25,
        }}>
          <Typography sx={{
            maxWidth: 1320, mx: 'auto',
            fontFamily: FONT.mono, fontSize: 11, color: CB.danger,
          }}>
            {saveError} The board has been reloaded from the server.
          </Typography>
        </Box>
      ) : null}

      {/* --- workspace ---
          Above 900px: two columns, panel docked on the right.
          Below: one column, and the panel floats over the board instead of
          stacking under it. Stacking is what this page did first, and it made
          the details panel invisible on a tablet — a full board-height below
          the fold, so tapping a card looked like nothing happening. The editor
          settled this at the same width; see the note in corkTokens.js. */}
      <Box sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '1fr',
        [BP.docked]: { gridTemplateColumns: `1fr ${DOCKED_PANEL_W}px` },
        alignItems: 'stretch',
        // Clips the parked panel. Below 900px it waits at translateX(100%) —
        // its full width past the right edge — and without this the *document*
        // grows to include it, so the whole page scrolls sideways to reveal a
        // panel that is supposed to be hidden.
        overflowX: 'hidden',
      }}>
        {view === 'board' ? (
          <CorkBoardStage
            nodes={board.nodes}
            edges={board.edges}
            selectedId={selectedId}
            showAllConnections={showAllConnections}
            onSelect={selectCard}
            onMoveNode={moveNode}
            emptyState={emptyState}
          />
        ) : (
          <FlowDiagram
            nodes={board.nodes}
            edges={board.edges}
            chapters={chapters}
            selectedId={selectedId}
            showAllConnections={showAllConnections}
            onSelect={selectCard}
            emptyState={emptyState}
          />
        )}


        {/* The handle that summons the panel, below 900px only. Above it the
            panel is docked and a control to reveal it would have no job —
            the same reasoning as `.rail-handle` in EditorPage.css. */}
        <Box
          component="button"
          type="button"
          aria-label={panelOpen ? 'Hide details' : 'Show details'}
          aria-expanded={panelOpen}
          onClick={() => setPanelOpen((v) => !v)}
          sx={{
            display: 'none',
            [BP.panelFloats]: { display: 'flex' },
            position: 'absolute', top: '50%', right: 0, zIndex: 31,
            transform: 'translateY(-50%)',
            alignItems: 'center', justifyContent: 'center',
            width: 20, height: 56, p: 0, cursor: 'pointer',
            border: `1px solid ${CB.lineSoft}`, borderRight: 'none',
            borderRadius: '4px 0 0 4px',
            backgroundColor: CB.white, color: CB.purple,
            boxShadow: '-4px 0 12px rgba(20,10,40,0.18)',
          }}
        >
          {panelOpen
            ? <ChevronRightIcon sx={{ fontSize: 16 }} />
            : <ChevronLeftIcon sx={{ fontSize: 16 }} />}
        </Box>

        <Box data-testid="details-panel" sx={{
          backgroundColor: CB.white,
          borderTop: `1px solid ${CB.lineSoft}`,
          overflowY: 'auto',
          [BP.docked]: {
            borderTop: 'none',
            borderLeft: `1px solid ${CB.lineSoft}`,
          },
          [BP.panelFloats]: {
            position: 'absolute', top: 0, right: 0, bottom: 0, zIndex: 30,
            width: FLOATING_PANEL_W,
            borderTop: 'none',
            borderLeft: `1px solid ${CB.lineSoft}`,
            boxShadow: '-8px 0 24px rgba(20,10,40,0.35)',
            transform: panelOpen ? 'none' : 'translateX(100%)',
            transition: 'transform .18s ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          },
        }}>
          <ManuscriptPreview
            chapters={chapters}
            onOpen={() => navigate(`/editor/${id}`)}
          />
          {creatingType ? (
            <CorkCreatePanel
              type={creatingType}
              board={board}
              chapters={chapters}
              onCreate={createNode}
              onCancel={() => setCreatingType(null)}
            />
          ) : (
            <CorkDetailsPanel
              node={selected}
              board={board}
              chapters={chapters}
              onPatchNode={patchNode}
              onSaveNode={saveNode}
              onRemoveNode={removeNode}
              onAddRelationship={addRelationship}
              onRemoveRelationship={removeRelationship}
            />
          )}
        </Box>
      </Box>

      <CorkAddCluster
        activeType={creatingType}
        panelOpen={panelOpen}
        onAdd={(type) => {
          setCreatingType(type);
          setSelectedId(null);
          setPanelOpen(true);
        }}
      />
    </Box>
  );
}
